import { HttpClient, HttpRequest, HttpHeaders, HttpEventType, HttpResponse } from '@angular/common/http';
import { InjectionToken, Injectable, Inject } from '@angular/core';
import { buildUrl, readBlob } from '@wizbii/angular-utilities';
import { toContact, WizFile, City } from '@wizbii/models';
import { map, switchMap, mergeMap } from 'rxjs/operators';
import { zip, of } from 'rxjs';
import { toRecruiter, toInvitation, toCompany, toApplication, toUtm, toComment, toEmail } from '@ats/models';

var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var AuthenticationApiError;
(function (AuthenticationApiError) {
    AuthenticationApiError["InvalidOldPasswordException"] = "InvalidOldPasswordException";
    AuthenticationApiError["PasswordTooWeakException"] = "PasswordTooWeakException";
})(AuthenticationApiError || (AuthenticationApiError = {}));
const AUTHENTICATION_API_CONFIG = new InjectionToken('AUTHENTICATION_API_CONFIG');
let AuthenticationWebservice = class AuthenticationWebservice {
    constructor(http, { appId, baseUrl }) {
        this.http = http;
        this.baseUrl = baseUrl;
        this.appId = appId;
    }
    refreshToken(tokens, options) {
        return this.http.post(`${this.baseUrl}/v1/jwt/refresh`, {
            appId: this.appId,
            token: tokens.token,
            refreshToken: tokens.refreshToken,
        }, options);
    }
    signIn(username, password, options) {
        return this.http.post(`${this.baseUrl}/auth`, {
            username,
            password,
            appId: this.appId,
        }, options);
    }
    getRecoveryEmail(email, options) {
        return this.http.post(`${this.baseUrl}/v1/user/lost-password`, {
            email,
            appId: this.appId,
        }, options);
    }
    recoverPassword(passwordToken, password, options) {
        return this.http.post(buildUrl(`${this.baseUrl}/v1/user/lost-password/:passwordToken`, { passwordToken }), {
            password,
            appId: this.appId,
        }, options);
    }
    getUserOverview(id, options) {
        return this.http.get(buildUrl(`${this.baseUrl}/v1/user/:id/overview`, { id }), options);
    }
    changeEmail(id, email, options) {
        return this.http.put(buildUrl(`${this.baseUrl}/v1/user/:id/email`, { id, email }), {
            email,
            appId: this.appId,
        }, options);
    }
    changePassword(id, oldPassword, newPassword, options) {
        return this.http.put(buildUrl(`${this.baseUrl}/v1/user/:id/password`, { id }), {
            oldPassword,
            newPassword,
            appId: this.appId,
        }, options);
    }
};
AuthenticationWebservice = __decorate([
    Injectable(),
    __param(1, Inject(AUTHENTICATION_API_CONFIG)),
    __metadata("design:paramtypes", [HttpClient, Object])
], AuthenticationWebservice);

var __decorate$1 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata$1 = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param$1 = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
const CONTACT_API_CONFIG = new InjectionToken('CONTACT_API_CONFIG');
let ContactWebservice = class ContactWebservice {
    constructor(http, { appId, baseUrl }) {
        this.http = http;
        this.baseUrl = baseUrl;
        this.appId = appId;
    }
    send(contact, options) {
        return this.http
            .post(`${this.baseUrl}/v1/contact`, Object.assign({}, contact, { appId: this.appId }), options)
            .pipe(map(toContact));
    }
};
ContactWebservice = __decorate$1([
    Injectable(),
    __param$1(1, Inject(CONTACT_API_CONFIG)),
    __metadata$1("design:paramtypes", [HttpClient, Object])
], ContactWebservice);

var __decorate$2 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata$2 = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param$2 = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
const FILE_API_CONFIG = new InjectionToken('FILE_API_CONFIG');
let FileWebservice = class FileWebservice {
    constructor(http, { baseUrl, googleStorageUrl, wizbiiFilesBucket }) {
        this.http = http;
        this.baseUrl = baseUrl;
        this.googleStorageUrl = googleStorageUrl;
        this.wizbiiFilesBucket = wizbiiFilesBucket;
    }
    create(file, options) {
        const res = /\.(\w+)$/.exec(file.domFile.name);
        const uploadUrlResponse$ = this.http.post(`${this.baseUrl}/v1/file`, {
            fileExtension: res && res.length > 0 ? res[1] : '',
            contentType: file.domFile.type,
            public: true,
        }, options);
        const buffer$ = readBlob(file.domFile);
        return zip(buffer$, uploadUrlResponse$).pipe(switchMap(([buffer, uploadUrlResponse]) => {
            const req = new HttpRequest('PUT', uploadUrlResponse.uploadUrl, buffer, {
                headers: new HttpHeaders(Object.assign({}, (options ? options.headers : {}), { 'Content-Type': file.domFile.type })),
                reportProgress: true,
            });
            return this.http.request(req).pipe(mergeMap((event) => event.type === HttpEventType.Response
                ? this.http.post(uploadUrlResponse.successUrl, null, options).pipe(map(() => new HttpResponse({
                    body: new WizFile(Object.assign({}, file, { id: uploadUrlResponse.fileId })),
                })))
                : of(event)));
        }));
    }
    getDownloadBlob(fileId, options) {
        const url = `${this.googleStorageUrl}/${this.wizbiiFilesBucket}/${fileId}`;
        return this.http.get(url, Object.assign({}, options, { responseType: 'blob' }));
    }
    delete(fileId, options) {
        const url = `${this.baseUrl}/v1/file/${fileId}`;
        return this.http.delete(url, options);
    }
};
FileWebservice = __decorate$2([
    Injectable(),
    __param$2(1, Inject(FILE_API_CONFIG)),
    __metadata$2("design:paramtypes", [HttpClient, Object])
], FileWebservice);

var __decorate$3 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata$3 = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param$3 = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
const PLACE_API_CONFIG = new InjectionToken('PLACE_API_CONFIG');
let CityWebservice = class CityWebservice {
    constructor(http, { baseUrl }) {
        this.http = http;
        this.baseUrl = baseUrl;
    }
    get(id, options) {
        return this.http
            .get(buildUrl(`${this.baseUrl}/v2/location/?:id`, { id }), options)
            .pipe(map(item => new City(item)));
    }
    getBy({ query, countries }, options) {
        const url = buildUrl(`${this.baseUrl}/v2/location`, Object.assign({}, (query ? { name: query } : {}), (countries && countries.length ? { countries: countries.join() } : {})));
        return this.http.get(url, options).pipe(map(items => (items || []).map(item => new City(item))));
    }
};
CityWebservice = __decorate$3([
    Injectable(),
    __param$3(1, Inject(PLACE_API_CONFIG)),
    __metadata$3("design:paramtypes", [HttpClient, Object])
], CityWebservice);

const RECRUITER_API_CONFIG = new InjectionToken('RECRUITER_API_CONFIG');

var __decorate$4 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata$4 = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param$4 = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
let RecruiterWebservice = class RecruiterWebservice {
    constructor(http, { baseUrl }) {
        this.http = http;
        this.baseUrl = baseUrl;
    }
    getByUserId(userId, options) {
        return this.http
            .get(buildUrl(`${this.baseUrl}/v1/recruiters/by-user-id/:userId`, { userId }), options)
            .pipe(map(toRecruiter));
    }
    getRecruiterById(recruiterId, options) {
        return this.http
            .get(buildUrl(`${this.baseUrl}/v1/recruiters/:recruiterId`, { recruiterId }), options)
            .pipe(map(toRecruiter));
    }
    getInvitations(options) {
        return this.http
            .get(buildUrl(`${this.baseUrl}/v1/invitations`), options)
            .pipe(map(it => it.map(toInvitation)));
    }
    addInvitation(email, options) {
        return this.http.post(buildUrl(`${this.baseUrl}/v1/invitations`), { email }, options);
    }
    update(recruiter, options) {
        return this.http
            .put(buildUrl(`${this.baseUrl}/v1/recruiters/:recruiterId`, { recruiterId: recruiter._id }), recruiter, options)
            .pipe(map(() => recruiter), map(toRecruiter));
    }
    findRecruitersByCompanyId(companyId, options) {
        return this.http.get(buildUrl(`${this.baseUrl}/v1/recruiters`, { companyId }), options).pipe(map(response => response.results), map(recruiters => recruiters.map(toRecruiter)));
    }
    addAuthorizations(recruiterId, authorizations, options) {
        return this.http.patch(buildUrl(`${this.baseUrl}/v1/recruiters/:recruiterId/authorization/add`, {
            recruiterId,
        }), { authorizations }, options);
    }
    deleteAuthorizations(recruiterId, authorizations, options) {
        return this.http.patch(buildUrl(`${this.baseUrl}/v1/recruiters/:recruiterId/authorization/delete`, {
            recruiterId,
        }), { authorizations }, options);
    }
};
RecruiterWebservice = __decorate$4([
    Injectable(),
    __param$4(1, Inject(RECRUITER_API_CONFIG)),
    __metadata$4("design:paramtypes", [HttpClient, Object])
], RecruiterWebservice);

var __decorate$5 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata$5 = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param$5 = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
let CompanyWebservice = class CompanyWebservice {
    constructor(http, { baseUrl }) {
        this.http = http;
        this.baseUrl = baseUrl;
    }
    get(id) {
        return this.http.get(buildUrl(`${this.baseUrl}/v1/companies/:id`, { id })).pipe(map(toCompany));
    }
    updateCompany(company) {
        return this.http
            .put(buildUrl(`${this.baseUrl}/v1/companies/:companyId`, { companyId: company._id }), company)
            .pipe(map(() => company), map(toCompany));
    }
};
CompanyWebservice = __decorate$5([
    Injectable(),
    __param$5(1, Inject(RECRUITER_API_CONFIG)),
    __metadata$5("design:paramtypes", [HttpClient, Object])
], CompanyWebservice);

var __decorate$6 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata$6 = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param$6 = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
const CANDIDATE_API_CONFIG = new InjectionToken('CANDIDATE_API_CONFIG');
let ApplicationWebservice = class ApplicationWebservice {
    constructor(http, { baseUrl }) {
        this.http = http;
        this.baseUrl = baseUrl;
    }
    get(id, options) {
        return this.http.get(buildUrl(`${this.baseUrl}/v1/applications/:id`, { id }), options).pipe(map(toApplication));
    }
    getStatus(options) {
        return this.http
            .get(`${this.baseUrl}/v1/applications/status`, options)
            .pipe(map(response => response.results));
    }
    getSourcesPlatform(options) {
        return this.http
            .get(`${this.baseUrl}/v1/applications/source-platform`, options)
            .pipe(map(response => response.results), map(sources => sources.map(source => ({ name: source.name, utm: toUtm(source.utm) }))));
    }
    findApplications(jobId, options) {
        return this.http.get(buildUrl(`${this.baseUrl}/v1/applications`, { jobId }), options).pipe(map(response => response.results), map(applications => applications.map(toApplication)));
    }
    updateStatus(applicationId, status, options) {
        return this.http.patch(buildUrl(`${this.baseUrl}/v1/applications/:applicationId/status/:status`, {
            applicationId,
            status,
        }), null, options);
    }
    addComment(id, payload, options) {
        return this.http
            .post(buildUrl(`${this.baseUrl}/v1/applications/:id/comment`, { id }), payload, options)
            .pipe(map(toComment));
    }
    deleteComment(applicationId, commentId, options) {
        return this.http.delete(buildUrl(`${this.baseUrl}/v1/applications/:applicationId/comment/:commentId`, {
            applicationId,
            commentId,
        }), options);
    }
    patchComment(applicationId, commentId, content, options) {
        return this.http.patch(buildUrl(`${this.baseUrl}/v1/applications/:applicationId/comment/:commentId`, {
            applicationId,
            commentId,
        }), content, options);
    }
    sendEmail(applicationId, email, options) {
        return this.http
            .post(buildUrl(`${this.baseUrl}/v1/applications/:applicationId/contact`, {
            applicationId,
        }), { subject: email.subject, message: email.message }, options)
            .pipe(map(toEmail));
    }
    patchResume(applicationId, resumeFileId, options) {
        return this.http.patch(buildUrl(`${this.baseUrl}/v1/applications/:applicationId/resume/?:resumeFileId`, resumeFileId
            ? {
                applicationId,
                resumeFileId,
            }
            : { applicationId }), null, options);
    }
    patch(application, options) {
        return this.http
            .patch(buildUrl(`${this.baseUrl}/v1/applications/:id`, { id: application.id }), application, options)
            .pipe(map(() => application), map(toApplication));
    }
    deepPatch(source, form, application, jobId, options) {
        const applicationUpdated = this.formToResource(form, source, jobId, application);
        return this.http
            .patch(buildUrl(`${this.baseUrl}/v1/applications/:id`, { id: application.id }), applicationUpdated, options)
            .pipe(map(() => applicationUpdated), map(toApplication));
    }
    formToResource(form, source, jobId, application) {
        return !application
            ? toApplication({
                jobId,
                coverLetter: form.value.presentation,
                resumeFileId: form.value.cv === '' ? null : form.value.cv.id,
                status: form.value.status,
                utmSource: {
                    platform: 'App',
                    utm: {
                        campaign: jobId,
                        content: source.utm.content || null,
                        medium: source.utm.medium || null,
                        source: source.name,
                        term: source.utm.term || null,
                    },
                },
            })
            : toApplication(Object.assign({}, application, { jobId, coverLetter: form.value.presentation }, (!form.value.cv ? {} : { resumeFileId: form.value.cv.id }), (!form.value.status ? {} : { status: form.value.status }), { utmSource: {
                    platform: 'App',
                    utm: {
                        campaign: jobId,
                        content: source.utm.content || null,
                        medium: source.utm.medium || null,
                        source: source.name,
                        term: source.utm.term || null,
                    },
                } }));
    }
};
ApplicationWebservice = __decorate$6([
    Injectable(),
    __param$6(1, Inject(CANDIDATE_API_CONFIG)),
    __metadata$6("design:paramtypes", [HttpClient, Object])
], ApplicationWebservice);

export { AUTHENTICATION_API_CONFIG, ApplicationWebservice, AuthenticationApiError, AuthenticationWebservice, CANDIDATE_API_CONFIG, CONTACT_API_CONFIG, CityWebservice, CompanyWebservice, ContactWebservice, FILE_API_CONFIG, FileWebservice, PLACE_API_CONFIG, RECRUITER_API_CONFIG, RecruiterWebservice };
//# sourceMappingURL=wizbii-webservices.js.map
